import React, { useState, useEffect } from 'react';
import { X, Users, List, Monitor, MapPin } from 'lucide-react';

/**
 * EditScreenModal - Optimized separate modal component
 * 
 * Performance Benefits:
 * - State is isolated from parent Dashboard
 * - Only this modal re-renders when typing
 * - Dashboard doesn't re-render on every keystroke
 */
const EditScreenModal = ({ 
  screen, 
  isOpen, 
  onClose, 
  onSave,
  groups = [],
  playlists = [],
  t 
}) => {
  // Local state - doesn't trigger Dashboard re-renders
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    playlist_id: null,
    group_id: null
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset form data when screen changes
  useEffect(() => {
    if (screen) {
      setFormData({
        name: screen.name || '',
        location: screen.location || '',
        playlist_id: screen.playlist_id || null,
        group_id: screen.group_id || null
      });
      setError('');
    }
  }, [screen]);

  if (!isOpen || !screen) return null;

  const handleSave = async () => {
    // Validation
    if (!formData.name.trim()) {
      setError(t('screens.errors.nameRequired'));
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await onSave(screen.id, formData);
      onClose();
    } catch (err) {
      setError(err.message || t('screens.errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setError('');
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={handleClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md flex flex-col max-h-[90vh] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyPress}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <Monitor className="text-primary-600 dark:text-primary-400" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {t('screens.editScreen')}
              </h3>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Screen Name */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
              {t('screens.name')} *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                name: e.target.value 
              }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
              placeholder={t('screens.namePlaceholder')}
              autoFocus
              disabled={isSaving}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('screens.nameHelp')}
            </p>
          </div>

          {/* Location Input (Optional) */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
              {t('screens.location')} <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <div className="relative">
              <MapPin 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" 
                size={18} 
              />
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  location: e.target.value 
                }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg pl-10 pr-4 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
                placeholder={t('screens.locationPlaceholder')}
                disabled={isSaving}
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('screens.locationHelp')}
            </p>
          </div>

          {/* Assign Playlist */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
              {t('screens.assignPlaylist')}
            </label>
            <div className="relative">
              <List 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" 
                size={18} 
              />
              <select
                value={formData.playlist_id || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  playlist_id: e.target.value ? parseInt(e.target.value) : null 
                }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg pl-10 pr-4 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
                disabled={isSaving}
              >
                <option value="">{t('screens.noPlaylistAllMedia')}</option>
                {playlists.map(playlist => (
                  <option key={playlist.id} value={playlist.id}>
                    {playlist.name} ({playlist.media_items?.length || 0} {t('common.items')})
                  </option>
                ))}
              </select>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {formData.playlist_id 
                ? t('screens.screenWillPlayPlaylist')
                : t('screens.screenWillPlay')}
            </p>
          </div>

          {/* Assign Group */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
              {t('screens.assignGroup')}
            </label>
            <div className="relative">
              <Users 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" 
                size={18} 
              />
              <select
                value={formData.group_id || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  group_id: e.target.value ? parseInt(e.target.value) : null 
                }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg pl-10 pr-4 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
                disabled={isSaving}
              >
                <option value="">{t('screens.noGroup')}</option>
                {groups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.icon} {group.name}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('screens.optionalGroups')}
            </p>
          </div>

          {/* Technical Info - Collapsible */}
          <details className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <summary className="cursor-pointer bg-gray-50 dark:bg-gray-700/50 px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm">
              <span>{t('screens.currentStatus')}</span>
              <svg className="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div className="p-4 space-y-3 bg-white dark:bg-gray-800 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400 font-semibold">
                  {t('screens.status')}:
                </span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                  screen.status === 'online' 
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${screen.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  {screen.status === 'online' ? t('dashboard.online') : t('dashboard.offline')}
                </span>
              </div>
              
              {screen.last_seen && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400 font-semibold">
                    {t('screens.lastSeen')}:
                  </span>
                  <span className="text-gray-900 dark:text-gray-100 text-xs">
                    {new Date(screen.last_seen).toLocaleString()}
                  </span>
                </div>
              )}
              
              <div className="text-gray-600 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                <span className="font-semibold block mb-1">UUID:</span>
                <code className="block text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded break-all font-mono">
                  {screen.uuid}
                </code>
              </div>
            </div>
          </details>
        </div>

        {/* Footer - Sticky */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2.5 rounded-lg transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !formData.name.trim()}
              className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white px-4 py-2.5 rounded-lg transition-colors font-semibold disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('common.saving')}
                </>
              ) : (
                t('screens.saveChanges')
              )}
            </button>
          </div>
          
          {/* Keyboard Shortcuts Hint */}
          <div className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
            <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded">Enter</kbd> {t('common.toSave')} • 
            <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded ml-2">Esc</kbd> {t('common.toCancel')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditScreenModal;